import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Clock, User, Calendar, CheckCircle, AlertCircle, XCircle, Search, ClipboardList, Star, Phone, ChevronLeft, ChevronRight } from "lucide-react";
import tokenService from "../../../services/token-service";
import { ServiceCategoryModal } from "../modals/index";
import apiClient from "../../../services/api-client";
import apiUrls from "../../../config/api-urls";

const Jobs = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, active, completed
  const [searchQuery, setSearchQuery] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, pages: 0 });
  // eslint-disable-next-line no-unused-vars
  const token = tokenService.getToken();
  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;
  // eslint-disable-next-line no-unused-vars
  const userId = authUser?._id;

  useEffect(() => {
    fetchJobs();
  }, [filter, pagination.page]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };
      
      if (filter !== "all") {
        params.status = filter;
      }

      const response = await apiClient.get(apiUrls.services.getMyTasks, {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setJobs(response.data.data || []);
        setPagination({
          page: response.data.pagination.page,
          limit: response.data.pagination.limit,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages,
        });
      }
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending_offers: {
        label: "Pending Offers",
        color: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
        icon: Clock,
      },
      scheduled: {
        label: "Scheduled",
        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        icon: Calendar,
      },
      searching: {
        label: "Searching",
        color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
        icon: Search,
      },
      handyman_selected: {
        label: "Handyman Selected",
        color: "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400",
        icon: User,
      },
      assigned: {
        label: "Assigned",
        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        icon: CheckCircle,
      },
      on_the_way: {
        label: "On The Way",
        color: "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-400",
        icon: MapPin,
      },
      arrived: {
        label: "Arrived",
        color: "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400",
        icon: MapPin,
      },
      work_started: {
        label: "Work Started",
        color: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400",
        icon: AlertCircle,
      },
      in_progress: {
        label: "In Progress",
        color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
        icon: AlertCircle,
      },
      completed: {
        label: "Completed",
        color: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
        icon: CheckCircle,
      },
      cancelled: {
        label: "Cancelled",
        color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        icon: XCircle,
      },
      disputed: {
        label: "Disputed",
        color: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
        icon: AlertCircle,
      },
    };
    return configs[status] || configs.pending_offers;
  };

  const filteredJobs = jobs.filter((job) => {
    const matchesSearch =
      job.mainServiceId?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.subServiceIds?.some(sub => sub.name?.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  const handleJobClick = (job) => {
    const status = job?.status;

    if (status === "scheduled") {
      navigate("/customer/schedule");
      return;
    }
     if (status === "pending_offers") {
      navigate("/customer/service-request");
      return;
    }

    if (status === "completed" || status === "cancelled" || status === "disputed") {
      return;
    }

    const liveStatuses = new Set([
      "searching",
      "handyman_selected",
      "assigned",
      "on_the_way",
      "arrived",
      "work_started",
      "in_progress",
    ]);

    if (liveStatuses.has(status)) {
      navigate("/customer/service-request/live", {
        state: {
          task: job,
          serviceRequestId: job?._id,
        },
      });
      return;
    }
  };

  const isNavigableJob = (job) => {
    const status = job?.status;
    if (status === "scheduled") return true;
    if (status === "completed" || status === "cancelled" || status === "disputed") return false;
    return (
      status === "searching" ||
      status === "handyman_selected" ||
      status === "assigned" ||
      status === "on_the_way" ||
      status === "arrived" ||
      status === "work_started" ||
      status === "in_progress"
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
          <p className="text-gray-600 text-sm font-medium">Loading requests...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">My Service Requests</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Track and manage all your service requests
          </p>
        </div>

        <div className="flex flex-col items-end gap-3">
          <div className="flex flex-wrap items-center justify-end gap-2">
            {["all", "active", "completed", "cancelled", "disputed"].map((status) => (
              <button
                key={status}
                onClick={() => {
                  setFilter(status);
                  setPagination((prev) => ({ ...prev, page: 1 }));
                }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? "bg-[#EFA938] text-white"
                    : "bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-slate-700 hover:bg-gray-50 dark:hover:bg-slate-700"
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors"
            >
              New Request
            </button>
          </div>

          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-[#EFA938] focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Jobs List */}
      {filteredJobs.length > 0 ? (
        <div className="grid gap-4">
          {filteredJobs.map((job) => {
            const statusConfig = getStatusConfig(job.status);
            const StatusIcon = statusConfig.icon;

            const serviceName = job.mainServiceId?.name || "Service Request";
            const subServices = job.subServiceIds?.map(sub => sub.name).join(", ") || "";
            const handyman = job.selectedHandymanId;
            const location = job.location?.address || job.location?.city || "Location not specified";
            const scheduledDate = job.scheduledDate ? new Date(job.scheduledDate) : new Date(job.createdAt);
            const completedWork = job.completedWork;
            const completedWorkDescription = completedWork?.description;
            const completedWorkPictures = Array.isArray(completedWork?.pictures) ? completedWork.pictures : [];
            const completedWorkVideo = completedWork?.video;
            const dispute = job.dispute;
            const disputeRaisedByRole = dispute?.raisedByRole;
            const disputeReason = dispute?.reason;
            const disputeDescription = dispute?.description;
            const disputeStatus = dispute?.status;

            return (
              <div
                key={job._id}
                onClick={() => handleJobClick(job)}
                className={`bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700 p-3 transition-all ${
                  isNavigableJob(job) ? "hover:shadow-md cursor-pointer" : "cursor-default"
                }`}
              >
                <div className="flex flex-col gap-2">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="text-base font-bold text-gray-900 dark:text-white truncate">{serviceName}</h3>
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold flex-shrink-0 ${
                          job.serviceType === 'live' 
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400' 
                            : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
                        }`}>
                          {job.serviceType === 'live' ? 'Live' : 'Scheduled'}
                        </span>
                      </div>
                      {subServices && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 truncate">{subServices}</p>
                      )}
                    </div>
                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full ${statusConfig.color} flex-shrink-0`}>
                      <StatusIcon className="h-3 w-3" />
                      <span className="text-xs font-semibold">{statusConfig.label}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Description</p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">{job.description}</p>
                  </div>

                  {job.status === "cancelled" && (job.cancellationReason?.cancelledBy === "handyman"|| job.cancellationReason?.cancelledBy === "customer") && (
                    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                      <p className="text-xs font-bold text-red-700 dark:text-red-300">
                        Cancelled by {job.cancellationReason?.cancelledBy}
                      </p>

                      {job.cancellationReason.reason && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                          Reason: {job.cancellationReason.reason}
                        </p>
                      )}

                      {job.cancellationReason.cancelledAt && (
                        <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">
                          {new Date(job.cancellationReason.cancelledAt).toLocaleString()}
                        </p>
                      )}

                      {job.cancellationReason.penaltyApplied && (
                        <p className="mt-1 text-[11px] text-red-600 dark:text-red-300">
                          Penalty: PKR {Number(job.cancellationReason.penaltyAmount || 0).toLocaleString()}
                        </p>
                      )}
                    </div>
                  )}

                  {job.status === "disputed" && (disputeRaisedByRole || disputeReason || disputeDescription) && (
                    <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                      <p className="text-xs font-bold text-red-700 dark:text-red-300">Dispute</p>

                      {disputeStatus && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                          Status: {disputeStatus}
                        </p>
                      )}

                      {disputeRaisedByRole && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                          Raised by: {disputeRaisedByRole}
                        </p>
                      )}

                      {disputeReason && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                          Reason: {disputeReason}
                        </p>
                      )}

                      {disputeDescription && (
                        <p className="mt-1 text-xs text-red-700 dark:text-red-200">
                          Description: {disputeDescription}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Info Row */}
                  

                  {/* Price Info */}
                  <div className="flex items-center gap-3 flex-wrap">
                    {job.estimatedPrice && (
                      <div className="flex items-baseline gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Base Price:</span>
                        <span className="text-sm font-bold text-gray-700 dark:text-gray-300">
                          Rs {job.estimatedPrice?.toLocaleString()}
                        </span>
                      </div>
                    )}
                    {job.finalPrice && (
                      <div className="flex items-baseline gap-1">
                        <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Final Price:</span>
                        <span className="text-sm font-bold text-green-600 dark:text-green-400">
                          Rs {job.finalPrice?.toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Handyman Info */}
                  {handyman && (
                    <div className="pt-1.5 border-t border-gray-200 dark:border-slate-700">
                      <p className="text-xs font-bold text-gray-500 dark:text-gray-400 mb-0.5">Assigned Handyman</p>
                      <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 mb-1">
                        <MapPin className="h-3 w-3 text-[#EFA938] flex-shrink-0" />
                        <span className="truncate">{location}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                          <User className="h-3 w-3 text-[#EFA938] flex-shrink-0" />
                          <span className="text-xs font-medium text-gray-900 dark:text-white truncate">{handyman.fullName}</span>
                          {job.handymanRating && (
                            <span className="text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">⭐ {job.handymanRating.average}</span>
                          )}
                          {handyman.phoneNumber && (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400 flex-shrink-0">
                              <Phone className="h-3 w-3 text-[#EFA938]" />
                              {handyman.phoneNumber}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {job.customerReview && (
                    <div className="mt-2 rounded-lg border border-[#EFA938]/30 bg-[#FFF8EF] dark:bg-[#1f1a10] dark:border-[#EFA938]/30 p-3">
                      <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-[#EFA938] fill-[#EFA938]" />
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          Handyman Rating: {job.customerReview.rating ?? 0}/5
                        </p>
                      </div>

                      {job.customerReview.review && (
                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-200">
                          &quot;{job.customerReview.review}&quot;
                        </p>
                      )}
                    </div>
                  )}

                  {(job.status === "completed" || job.status === "disputed") && (completedWorkDescription || completedWorkPictures.length > 0 || completedWorkVideo) && (
                    <div className="mt-2 rounded-lg border border-green-200 dark:border-green-900/50 bg-green-50 dark:bg-green-900/10 p-3">
                      <p className="text-xs font-bold text-green-800 dark:text-green-300 mb-2">Completed Work</p>

                      {completedWorkDescription && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-0.5">Description</p>
                          <p className="text-sm text-gray-800 dark:text-gray-200">{completedWorkDescription}</p>
                        </div>
                      )}

                      {completedWorkPictures.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Pictures</p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {completedWorkPictures.map((url, idx) => (
                              <a
                                key={`${job._id}-completedWorkPicture-${idx}`}
                                href={url}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="block"
                              >
                                <img
                                  src={url}
                                  alt={`Completed work ${idx + 1}`}
                                  className="h-20 w-full rounded-md object-cover border border-gray-200 dark:border-slate-700"
                                  loading="lazy"
                                />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}

                      {completedWorkVideo && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">Video</p>
                          <div onClick={(e) => e.stopPropagation()}>
                            <video
                              src={completedWorkVideo}
                              controls
                              className="w-full max-h-56 rounded-md border border-gray-200 dark:border-slate-700 bg-black"
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Badges */}
                  {(job.status === "pending_offers" && job.offerCount > 0) || job.status === "scheduled" ? (
                    <div className="flex items-center gap-2">
                      {job.status === "pending_offers" && job.offerCount > 0 && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-semibold rounded-full">
                          {job.offerCount} Offer{job.offerCount > 1 ? 's' : ''}
                        </span>
                      )}
                      {job.status === "scheduled" && (
                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs font-semibold rounded-full">
                          Scheduled
                        </span>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-gray-200 dark:border-slate-700 p-12 text-center">
          <ClipboardList className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No requests found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {searchQuery ? "Try adjusting your search" : "Get started by creating your first service request"}
          </p>
          {!searchQuery && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-6 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors"
            >
              Create Request
            </button>
          )}
        </div>
      )}

      {/* Pagination Controls */}
      {pagination.pages > 1 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="px-6 py-4 bg-gray-50 dark:bg-slate-900">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Page Info */}
              <div className="text-sm text-gray-600 dark:text-gray-400">
                Showing page <span className="font-semibold text-gray-900 dark:text-white">{pagination.page}</span> of{" "}
                <span className="font-semibold text-gray-900 dark:text-white">{pagination.pages}</span>
                {" "}({pagination.total} total requests)
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.page === 1
                      ? "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                      : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                  }`}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="hidden sm:flex items-center gap-1">
                  {Array.from({ length: pagination.pages }, (_, i) => i + 1).map((pageNum) => {
                    // Show first page, last page, current page, and pages around current
                    const showPage =
                      pageNum === 1 ||
                      pageNum === pagination.pages ||
                      (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1);

                    const showEllipsis =
                      (pageNum === 2 && pagination.page > 3) ||
                      (pageNum === pagination.pages - 1 && pagination.page < pagination.pages - 2);

                    if (showEllipsis) {
                      return (
                        <span
                          key={pageNum}
                          className="px-2 text-gray-400 dark:text-gray-600"
                        >
                          ...
                        </span>
                      );
                    }

                    if (!showPage) return null;

                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                          pagination.page === pageNum
                            ? "bg-[#EFA938] text-white"
                            : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>

                <button
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    pagination.page === pagination.pages
                      ? "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600 cursor-not-allowed"
                      : "bg-white dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-600 border border-gray-200 dark:border-slate-600"
                  }`}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Service Category Modal */}
      <ServiceCategoryModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default Jobs;

